import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/use-translation";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { authErrorMessage } from "@/lib/auth-error";
import { tryLegacyPassword } from "@/lib/legacy-password";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export default function LoginPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoOpen, setSsoOpen] = useState(false);
  const [ssoDomain, setSsoDomain] = useState("");
  const [ssoLoading, setSsoLoading] = useState(false);

  const redirectTo = searchParams.get("redirect") ?? "/";

  if (user) {
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (!error) return;

    if (error.code !== "invalid_credentials") {
      toast.error(authErrorMessage(t, error));
      setLoading(false);
      return;
    }

    if (await tryLegacyPassword(email, password)) {
      const retry = await supabase.auth.signInWithPassword({ email, password });
      if (!retry.error) return;
      toast.error(authErrorMessage(t, retry.error));
      setLoading(false);
      return;
    }

    toast.error(authErrorMessage(t, error), {
      description: t("auth.legacyPasswordHint"),
    });
    setLoading(false);
  }

  async function handleGoogleSignIn() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin + redirectTo,
      },
    });

    if (error) {
      toast.error(authErrorMessage(t, error));
    }
  }

  async function handleSsoSignIn(e: React.FormEvent) {
    e.preventDefault();
    if (!ssoDomain.trim()) return;

    setSsoLoading(true);
    try {
      const checkRes = await fetch(
        `${API_URL}/api/v1/config/sso/domain/${encodeURIComponent(ssoDomain.trim().toLowerCase())}`,
      );
      if (!checkRes.ok) throw new Error("Failed to check SSO domain");
      const checkData = await checkRes.json();

      if (!checkData.data.has_sso) {
        toast.error(t("auth.ssoNotConfigured"));
        setSsoLoading(false);
        return;
      }

      const { error } = await supabase.auth.signInWithSSO({
        domain: ssoDomain.trim().toLowerCase(),
        options: {
          redirectTo: window.location.origin + redirectTo,
        },
      });

      if (error) {
        toast.error(authErrorMessage(t, error));
      }
    } catch {
      toast.error(t("auth.ssoFailed"));
    } finally {
      setSsoLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <HelpBanner />
      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium">{t("auth.signInTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Button type="button" variant="outline" onClick={handleGoogleSignIn} className="w-full">
              {t("auth.signInWithGoogle")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSsoOpen(true)}
              className="w-full"
            >
              {t("auth.signInWithSso")}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  {t("auth.orContinueWithEmail")}
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-medium">
                  {t("auth.email")}
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="password" className="text-sm font-medium">
                  {t("auth.password")}
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? t("auth.signingIn") : t("auth.signIn")}
              </Button>
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground underline hover:text-foreground"
                >
                  {t("auth.forgotPassword")}
                </Link>
              </div>
            </form>

            <p className="text-center text-xs text-muted-foreground">
              {t("auth.termsPrefix")}{" "}
              <a
                href="https://foodsight.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                {t("auth.termsOfService")}
              </a>{" "}
              {t("auth.termsAnd")}{" "}
              <a
                href="https://foodsight.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                {t("auth.privacyPolicy")}
              </a>
              .
            </p>
          </CardContent>
        </Card>
      </div>

      <Dialog open={ssoOpen} onOpenChange={setSsoOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("auth.ssoTitle")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSsoSignIn} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="sso-domain" className="text-sm font-medium">
                {t("auth.ssoDomain")}
              </label>
              <Input
                id="sso-domain"
                placeholder={t("auth.ssoDomainPlaceholder")}
                value={ssoDomain}
                onChange={(e) => setSsoDomain(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">{t("auth.ssoDomainHint")}</p>
            </div>
            <Button type="submit" disabled={ssoLoading} className="w-full">
              {ssoLoading ? t("auth.ssoRedirecting") : t("auth.ssoContinue")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HelpBanner() {
  const { t } = useTranslation();
  // Revealed on click, so a harvester that does not click never sees the number in the page.
  const [phoneShown, setPhoneShown] = useState(false);
  const phone = import.meta.env.VITE_LOGIN_HELP_PHONE;
  const bookingUrl = import.meta.env.VITE_LOGIN_HELP_BOOKING_URL;
  if (!phone || !bookingUrl) return null;

  return (
    <div className="border-b bg-muted px-4 py-3 text-center text-sm text-muted-foreground">
      <p className="font-medium text-foreground">{t("auth.helpBannerTitle")}</p>
      <p className="mx-auto mt-1 max-w-2xl">
        {t("auth.helpBannerBody")}{" "}
        {phoneShown ? (
          <a
            href={`tel:${phone.replace(/\s/g, "")}`}
            className="font-medium text-foreground underline"
          >
            {phone}
          </a>
        ) : (
          <button
            type="button"
            onClick={() => setPhoneShown(true)}
            className="font-medium text-foreground underline"
          >
            {t("auth.helpBannerShowPhone")}
          </button>
        )}{" "}
        {t("auth.helpBannerOr")}{" "}
        <a
          href={bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-foreground underline"
        >
          {t("auth.helpBannerBookMeeting")}
        </a>
        .
      </p>
    </div>
  );
}
